import React, { Component } from 'react'
import { Alert, Button, Card, Checkbox, Empty, Modal, Radio, Select, Space, Spin, Steps, Typography, message } from 'antd'
import { CopyOutlined, ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons'
import {
    getUserDeparts,
    getUserListForDropbox,
    getVibeOwnerAccessContext,
    getVibeOwnerAccessProjects,
    getVibeOwnerAccessStatus,
    saveVibeOwnerPagePermission,
} from '../../request'
import './index.less'

const { Title, Text, Paragraph } = Typography

export default class VibeOwnerAccess extends Component {
    state = {
        loading: true, error: '', projects: [], projectId: undefined, context: null, statusContext: null,
        selectedRouteIds: [], accessMode: undefined, subjectId: undefined,
        includeChildren: true, users: [], departments: [], saving: false,
    }

    componentDidMount() {
        const params = new URLSearchParams(this.props.location?.search || '')
        this.requestedProjectId = params.get('projectId')
        this.statusView = params.get('view') === 'status'
        if (this.statusView) {
            this.loadStatusContext()
            return
        }
        this.loadProjects()
    }

    loadStatusContext = async () => {
        if (!/^\d+$/.test(this.requestedProjectId || '')) {
            this.setState({ loading: false, error: '应用信息不完整，请返回工作台后重试' })
            return
        }
        this.setState({ loading: true, error: '' })
        try {
            const statusContext = await getVibeOwnerAccessStatus({ projectId: this.requestedProjectId })
            this.setState({ loading: false, statusContext })
        } catch (error) {
            this.setState({ loading: false, error: error?.message || '应用状态加载失败，请稍后重试' })
        }
    }

    loadProjects = async () => {
        this.setState({ loading: true, error: '' })
        try {
            const projects = await getVibeOwnerAccessProjects() || []
            const requested = projects.find(item => String(item.id) === String(this.requestedProjectId))
            const project = requested || projects[0]
            this.setState({ projects, projectId: project?.id, loading: false }, () => {
                if (project) this.loadContext(project.id)
            })
        } catch (error) {
            this.setState({ loading: false, error: error?.message || '应用列表加载失败，请稍后重试' })
        }
    }

    loadContext = async (projectId = this.state.projectId) => {
        if (!projectId) return
        this.setState({ loading: true, error: '', projectId })
        try {
            const context = await getVibeOwnerAccessContext({ projectId })
            const pageRoutes = (context?.routes || []).filter(route => route.routeType === 'PAGE')
            this.setState({ context, selectedRouteIds: pageRoutes.map(route => route.id), loading: false })
        } catch (error) {
            this.setState({ loading: false, error: error?.message || '权限设置加载失败，请稍后重试' })
        }
    }

    loadOptions = async mode => {
        this.setState({ accessMode: mode, subjectId: undefined })
        if (mode === 'ALL' || (this.state.users.length && this.state.departments.length)) return
        try {
            const [users, departments] = await Promise.all([
                getUserListForDropbox({ type: 0 }), getUserDeparts({}),
            ])
            this.setState({ users: users || [], departments: departments || [] })
        } catch (error) {
            message.error(error?.message || '授权对象加载失败')
        }
    }

    copyPrompt = async () => {
        try {
            await navigator.clipboard.writeText(this.state.context?.routeSyncPrompt || '')
            message.success('页面同步提示词已复制，请在项目 Codex 中执行')
        } catch (error) {
            message.error('复制失败，请检查浏览器剪贴板权限')
        }
    }

    saveGrant = async () => {
        const { projectId, selectedRouteIds, accessMode, subjectId, includeChildren } = this.state
        if (!accessMode) {
            message.warning('请选择访问范围')
            return
        }
        if (!subjectId && accessMode !== 'ALL') {
            message.warning('请选择要授权的员工或部门')
            return
        }
        this.setState({ saving: true })
        try {
            await saveVibeOwnerPagePermission({
                projectId, routeIds: selectedRouteIds, subjectType: accessMode,
                subjectId: accessMode === 'ALL' ? '' : String(subjectId), includeChildren, granted: true,
            })
            message.success('权限设置已保存，正在进入应用')
            window.location.assign(this.state.context.entryUrl)
        } catch (error) {
            this.setState({ saving: false })
            message.error(error?.message || '保存失败，请稍后重试')
        }
    }

    allowAllEmployees = () => {
        const routes = (this.state.context?.routes || []).filter(route => route.routeType === 'PAGE')
        Modal.confirm({
            title: '允许全部员工访问所有页面？',
            content: `确认后，全体员工将可以访问当前已登记的 ${routes.length} 个页面。`,
            okText: '确认开放', cancelText: '取消',
            onOk: async () => {
                this.setState({ saving: true })
                try {
                    await saveVibeOwnerPagePermission({
                        projectId: this.state.projectId,
                        routeIds: routes.map(route => route.id),
                        subjectType: 'ALL', subjectId: '', includeChildren: false, granted: true,
                    })
                    message.success('已允许全部员工访问所有页面，正在进入应用')
                    window.location.assign(this.state.context.entryUrl)
                } catch (error) {
                    this.setState({ saving: false })
                    message.error(error?.message || '保存失败，请稍后重试')
                    throw error
                }
            },
        })
    }

    renderStatus = () => {
        const { loading, error, statusContext } = this.state
        return (
        <div className="vibe-owner-access-page"><Card className="vibe-owner-access-status">
            <Spin spinning={loading}>
                <SafetyCertificateOutlined className="vibe-owner-access-status-icon" />
                <Title level={3}>你还不能访问这个页面</Title>
                {error ? <Alert type="error" showIcon message={error} /> : <>
                    <Paragraph type="secondary">{statusContext?.projectName || '该应用'}正在准备访问设置，或你尚未获得页面权限。</Paragraph>
                    {statusContext?.ownerUserNick && <Paragraph>应用负责人：{statusContext.ownerUserNick}</Paragraph>}
                </>}
                <Space className="vibe-owner-access-status-actions">
                    <Button type="primary" href="/portal/dashboard">返回工作台</Button>
                    <Button disabled={!statusContext?.entryUrl} onClick={() => window.location.assign(statusContext.entryUrl)}>重新检查访问权限</Button>
                    {error && <Button onClick={this.loadStatusContext}>重试加载</Button>}
                </Space>
            </Spin>
        </Card></div>
        )
    }

    render() {
        if (this.statusView) return this.renderStatus()
        const { loading, error, projects, projectId, context, selectedRouteIds, accessMode, saving } = this.state
        const routes = (context?.routes || []).filter(route => route.routeType === 'PAGE')
        return <div className="vibe-owner-access-page">
            <div className="vibe-owner-access-heading"><Title level={3}>应用权限设置</Title>
                <Text type="secondary">应用尚未配置访问范围，请确认页面并设置谁可以访问。</Text></div>
            <Card bordered={false}>
                <Spin spinning={loading}>
                    {error && <Alert type="error" showIcon message={error} action={<Button onClick={this.loadProjects}>重试</Button>} />}
                    {!error && !loading && !projects.length && <Empty description="你还没有可配置的内部应用" />}
                    {!!projects.length && <>
                        <div className="vibe-owner-access-project"><Text>我创建的应用</Text>
                            <Select value={projectId} onChange={this.loadContext} style={{ minWidth: 260 }}
                                options={projects.map(item => ({ value: item.id, label: item.projectName }))} /></div>
                        <Steps current={routes.length ? 1 : 0} items={[{ title: '同步应用页面' }, { title: '设置访问范围' }, { title: '完成并进入应用' }]} />
                        {context && !routes.length && <Alert className="vibe-owner-access-alert" type="warning" showIcon
                            message="还差一步即可开放应用"
                            description="尚未检测到应用页面。复制提示词到项目 Codex 执行，完成后重新检测。"
                            action={<Space direction="vertical"><Button type="primary" icon={<CopyOutlined />} onClick={this.copyPrompt}>复制页面同步提示词</Button>
                                <Button icon={<ReloadOutlined />} onClick={() => this.loadContext()}>我已同步，重新检测</Button></Space>} />}
                        {!!routes.length && <>
                            <div className="vibe-owner-access-routes"><Title level={5}>选择页面</Title>
                                <Checkbox.Group value={selectedRouteIds} onChange={value => this.setState({ selectedRouteIds: value })}>
                                    <Space direction="vertical">{routes.map(route => <Checkbox key={route.id} value={route.id}>{route.routeName} <Text type="secondary">{route.pathPattern}</Text></Checkbox>)}</Space>
                                </Checkbox.Group></div>
                            <div className="vibe-owner-access-grant"><Title level={5}>访问范围</Title>
                                <Radio.Group value={accessMode} onChange={event => this.loadOptions(event.target.value)}>
                                    <Radio value="ALL">全部员工</Radio>
                                    <Radio value="USER">指定员工</Radio><Radio value="DEPARTMENT">指定部门</Radio>
                                </Radio.Group>
                                {accessMode === 'USER' && <Select showSearch placeholder="选择员工" value={this.state.subjectId} onChange={subjectId => this.setState({ subjectId })}
                                    options={this.state.users.map(user => ({ value: user.USER_ID || user.userId, label: user.USER_NICK || user.userNick || user.USER_NAME }))} />}
                                {accessMode === 'DEPARTMENT' && <Space><Select showSearch placeholder="选择部门" value={this.state.subjectId} onChange={subjectId => this.setState({ subjectId })}
                                    options={this.state.departments.map(dept => ({ value: dept.DING_DEPT_ID || dept.dingDeptId, label: dept.DEPT_NAME || dept.deptName }))} />
                                    <Checkbox checked={this.state.includeChildren} onChange={event => this.setState({ includeChildren: event.target.checked })}>包含下级部门</Checkbox></Space>}
                                <Paragraph type="secondary">项目创建人始终可以访问，无需单独授权。</Paragraph>
                            </div>
                            <Space>
                                <Button type="primary" size="large" loading={saving} onClick={this.allowAllEmployees}>允许全部员工访问所有页面</Button>
                                <Button size="large" loading={saving} disabled={!selectedRouteIds.length || !accessMode} onClick={this.saveGrant}>保存所选权限并进入应用</Button>
                            </Space>
                        </>}
                    </>}
                </Spin>
            </Card>
        </div>
    }
}
